import React from "react";
import {
  Box,
  CircularProgress,
  Typography,
  alpha,
  useTheme
} from "@mui/material";

const PageLoadingOverlay = ({
  open,
  message = "Updating schedule numbers...",
  subMessage
}) => {
  const theme = useTheme();

  if (!open) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: alpha(theme.palette.background.default, 0.7),
        backdropFilter: "blur(4px)",
        animation: "fadeIn 0.2s ease",
        "@keyframes fadeIn": {
          "0%": { opacity: 0 },
          "100%": { opacity: 1 }
        }
      }}
    >
      {/* 加载动画容器 */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 3,
          p: 4,
          borderRadius: 3,
          bgcolor: theme.palette.background.paper,
          boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.2)}`,
          border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
          minWidth: 280,
          animation: "slideUp 0.3s ease",
          "@keyframes slideUp": {
            "0%": { transform: "translateY(16px)", opacity: 0 },
            "100%": { transform: "translateY(0)", opacity: 1 }
          }
        }}
      >
        {/* 旋转 Loading + 进度环 */}
        <Box sx={{ position: "relative", display: "flex" }}>
          <CircularProgress
            size={56}
            thickness={4}
            sx={{
              color: theme.palette.primary.main,
              animation: "rotate 1.4s linear infinite",
              "@keyframes rotate": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" }
              }
            }}
          />
          {/* 内圈小点动画 */}
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: theme.palette.primary.main,
              animation: "pulse 1.5s ease-in-out infinite",
              "@keyframes pulse": {
                "0%, 100%": {
                  transform: "translate(-50%, -50%) scale(1)",
                  opacity: 1
                },
                "50%": {
                  transform: "translate(-50%, -50%) scale(1.5)",
                  opacity: 0.6
                }
              }
            }}
          />
        </Box>

        {/* 主文案 */}
        <Typography
          variant='h6'
          fontWeight={600}
          color='text.primary'
          sx={{ textAlign: "center", letterSpacing: "-0.3px" }}
        >
          {message}
        </Typography>

        {/* 副文案（可选） */}
        {subMessage && (
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ textAlign: "center", maxWidth: "240px" }}
          >
            {subMessage}
          </Typography>
        )}

        {/* 底部进度条示意 */}
        <Box
          sx={{
            width: "100%",
            height: 4,
            bgcolor: alpha(theme.palette.action.hover, 0.5),
            borderRadius: 2,
            overflow: "hidden",
            mt: 1
          }}
        >
          <Box
            sx={{
              height: "100%",
              width: "60%",
              bgcolor: theme.palette.primary.main,
              borderRadius: 2,
              animation: "progress 1.2s ease-in-out infinite",
              "@keyframes progress": {
                "0%": { transform: "translateX(-100%)" },
                "50%": { transform: "translateX(80%)" },
                "100%": { transform: "translateX(100%)" }
              }
            }}
          />
        </Box>
      </Box>

      {/* 底部提示文案 */}
      <Typography
        variant='caption'
        color='text.disabled'
        sx={{
          mt: 3,
          fontStyle: "italic",
          animation: "fadeDelay 0.3s ease 0.2s forwards",
          opacity: 0,
          "@keyframes fadeDelay": {
            to: { opacity: 1 }
          }
        }}
      >
        Please do not close or refresh the page...
      </Typography>
    </Box>
  );
};

export default PageLoadingOverlay;
